import axios from "axios";
import config from 'react-global-configuration';
import CustomHeaders from "./CustomHeaders";
import UploadQueue from "../helpers/UploadQueue";

const UPLOAD_MAX_RETRIES = 3;
const UPLOAD_RETRY_BASE_DELAY = 1000; // ms: 1s, 2s, 4s (+ jitter)

class MediaRepository {

    static getEmbed(url) {
        // return new Promise((resolve, reject) => {
        //     axios.get(
        //         'https://www.youtube.com/oembed?url=' + url + '&format=json'
        //     )
        //         .then(response => {
        //             resolve(response)
        //         })
        //         .catch(error => {
        //             reject(error)
        //         })
        // })
        return new Promise((resolve, reject) => {
            axios.get(
                // 'https://noembed.com/embed?format=json&url=' + encodeURI(url) + '&maxwidth=960&maxheight=540',
                'https://noembed.com/embed?format=json&url=' + encodeURI(url)
            )
                .then(response => {
                    if (response.data.html) {
                        response.data.html = response.data.html.replace(/width="[^"]*"/, '').replace(/height="[^"]*"/, '');
                    }

                    if (response !== undefined && response.data !== undefined && response.data.thumbnail_url !== undefined && response.data.provider_name === "YouTube") {
                        response.data.thumbnail_url = response.data.thumbnail_url.replace('hqdefault', 'mqdefault')
                    }
                    resolve(response)
                })
                .catch(error => reject(error))
        })
    }

    static upload = (file, collection, module, moduleId, options = null, name = null, settings = null) => {
        const data = new FormData();
        data.append('file', file);
        data.append('type', collection != null ? collection : 'photo_album');
        data.append('module', module);

        if (moduleId) data.append('module_id', moduleId);
        if (name != null) data.append('name', name)
        if (settings != null) data.append('settings', settings)

        const headers = CustomHeaders.get({ website_id: module === 'website' ? moduleId : null });

        const attempt = (retry) => {
            // Track whether any part of the body reached the server: if not, a retry
            // can never create a duplicate media item.
            let bytesSent = 0;

            const requestOptions = {
                ...options,
                headers: headers,
                onUploadProgress: (progressEvent) => {
                    bytesSent = progressEvent.loaded;
                    if (options && typeof options.onUploadProgress === 'function') {
                        options.onUploadProgress(progressEvent);
                    }
                }
            };

            return MediaRepository.getUploadQueue()
                .run(() => axios.post(config.get("api_url") + '/media/upload', data, requestOptions))
                .catch(error => {
                    if (retry < UPLOAD_MAX_RETRIES && MediaRepository.isRetryableUploadError(error, bytesSent)) {
                        // Wait outside the queue so other uploads can use the slot
                        const delay = UPLOAD_RETRY_BASE_DELAY * Math.pow(2, retry) + Math.random() * 500;
                        return new Promise(resolve => setTimeout(resolve, delay)).then(() => attempt(retry + 1));
                    }
                    throw error;
                });
        };

        return new Promise((resolve, reject) => {
            attempt(0)
                .then(response => {
                    if (response.status !== 200) {
                        reject(response);
                    }
                    resolve(response);
                })
                .catch(error => reject(error))
        })
    }

    static getUploadQueue = () => {
        if (!MediaRepository._uploadQueue) {
            MediaRepository._uploadQueue = new UploadQueue(config.get('upload_concurrency', 2));
        }
        return MediaRepository._uploadQueue;
    }

    /**
     * Only retry when the request certainly did not create anything on the server:
     * - network error (e.g. rejected preflight -> reported as CORS/network error)
     *   before any byte of the body was sent
     * - explicit "busy" status codes from the server/proxy
     */
    static isRetryableUploadError = (error, bytesSent) => {
        if (!error || axios.isCancel(error)) return false;

        if (!error.response) {
            return bytesSent === 0;
        }

        return [429, 502, 503, 504].indexOf(error.response.status) > -1;
    }

    static uploadExternal(url, type, moduleId, name, module = null) {
        if (url.substr(0, 8) !== 'https://' || url.substr(0, 7) === 'http://') {
            url = 'https://' + url;
        }

        let data = { url: url, type: type, name: name };
        if (module) {
            data = { ...data, ...{ module: module, moduleId: moduleId } };
        } else {
            data = { ...data, ...{ page: moduleId } };
        }

        return new Promise((resolve, reject) => {
            axios.post(
                config.get('api_url') + '/media/external/upload', data, {
                headers: CustomHeaders.get()
            })
                .then(response => resolve(response))
                .catch(error => reject(error))
        })
    }

    static updateOrder(order, type, module, moduleId) {
        return new Promise((resolve, reject) => {
            axios.post(
                config.get('api_url') + '/media/update/order',
                {
                    order: JSON.stringify(order),
                    type: type,
                    module: module,
                    module_id: moduleId
                },
                {
                    headers: CustomHeaders.get()
                }
            )
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        })
    }

    static delete(id, type, moduleId, module = null) {
        return new Promise((resolve, reject) => {
            let data = { id: id, type: type };
            if (module) {
                data = { ...data, ...{ moduleId: moduleId, module: module } };
            } else {
                data = { ...data, ...{ page: moduleId } };
            }

            axios.post(
                config.get('api_url') + '/media/delete', data,
                {
                    headers: CustomHeaders.get()
                }
            )
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        })
    }

    static getSpecificSettings(type, moduleId = null, module = null) {
        return new Promise((resolve, reject) => {
            axios.get(
                config.get('api_url') + '/media/settings?type=' + type + (moduleId ? (module ? '&module=' : '&page=') + moduleId : ''),
                {
                    headers: CustomHeaders.get()
                }
            )
                .then(response => {
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                })
        })
    }

    static getAlbum(id, album, module) {
        return new Promise((resolve, reject) => {
            axios.get(
                config.get('api_url') + '/media/all?type=' + album + '&module=' + module + '&moduleId=' + id,
                {
                    headers: CustomHeaders.get()
                }
            )
                .then(response => resolve(response))
                .catch(error => reject(error))
        })
    }

    static quickUpdate = (data) => {
        return new Promise((resolve, reject) => {
            axios.post(
                config.get('api_url') + '/media/quick-update', data,
                { headers: CustomHeaders.get() }
            )
                .then(response => resolve(response))
                .catch(error => reject(error))
        });
    };
}

export default MediaRepository;
